import React, { useState } from 'react';
import { cn, formatCompact, formatNumber } from '../lib/utils';

/**
 * AreaChart — gradient-filled line chart with axes and hover tooltip.
 *
 * props:
 *  data: [{ label, value, value2? }, ...]   — if value2 provided, stacks
 *  height: number
 *  series: [{ key, label, color }]           — by default [{ key: 'value', label: 'Calls', color: 'chart-1' }]
 *  formatValue: fn(value) => string          — defaults to formatCompact
 *  showGrid: bool
 *  showAxes: bool
 *  yMax: number | null                      — auto-fit if null
 */
export function AreaChart({
  data = [],
  height = 240,
  series,
  formatValue = formatCompact,
  showGrid = true,
  showAxes = true,
  yMax = null,
  className,
}) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const series_ = series || [
    { key: 'value', label: 'Value', color: 'hsl(var(--chart-1))' },
  ];

  if (!data.length) {
    return (
      <div
        className="flex items-center justify-center text-xs text-fg-muted"
        style={{ height }}
      >
        No data
      </div>
    );
  }

  const width = 800;       // viewBox width; scales to container
  const padL = showAxes ? 36 : 8;
  const padR = 8;
  const padT = 12;
  const padB = showAxes ? 24 : 8;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  const computedMax = yMax ?? Math.max(
    ...data.flatMap((d) => series_.map((s) => d[s.key] || 0)),
    1
  );

  // Map data to points
  const points = (key) =>
    data.map((d, i) => {
      const x = padL + (i / Math.max(data.length - 1, 1)) * innerW;
      const y = padT + innerH - ((d[key] || 0) / computedMax) * innerH;
      return [x, y];
    });

  // Build paths per series
  const buildPaths = (key) => {
    const pts = points(key);
    if (pts.length < 2) return null;
    const line = pts
      .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
      .join(' ');
    const area = `${line} L ${pts[pts.length - 1][0]} ${padT + innerH} L ${pts[0][0]} ${padT + innerH} Z`;
    return { line, area, pts };
  };

  // Y-axis ticks
  const yTickCount = 4;
  const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) => {
    const v = (computedMax * i) / yTickCount;
    const y = padT + innerH - (i / yTickCount) * innerH;
    return { v, y };
  });

  return (
    <div className={cn('w-full', className)} style={{ aspectRatio: `${width} / ${height}` }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height="100%"
        className="overflow-visible block"
        onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          {series_.map((s, i) => (
            <linearGradient
              key={s.key}
              id={`areafill-${s.key}-${data.length}-${i}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={s.color} stopOpacity="0.32" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {/* Grid */}
        {showGrid && yTicks.map((tick, i) => (
          <line
            key={`grid-${i}`}
            x1={padL}
            x2={padL + innerW}
            y1={tick.y}
            y2={tick.y}
            stroke="hsl(var(--border))"
            strokeDasharray={i === yTickCount ? '0' : '2 3'}
            strokeOpacity={i === yTickCount ? 0.6 : 0.4}
          />
        ))}

        {/* Areas */}
        {series_.map((s, i) => {
          const paths = buildPaths(s.key);
          if (!paths) return null;
          return (
            <g key={`area-${s.key}`}>
              <path
                d={paths.area}
                fill={`url(#areafill-${s.key}-${data.length}-${i})`}
              />
              <path
                d={paths.line}
                fill="none"
                stroke={s.color}
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          );
        })}

        {/* Y axis labels */}
        {showAxes && yTicks.map((tick, i) => (
          <text
            key={`ylbl-${i}`}
            x={padL - 8}
            y={tick.y + 3}
            textAnchor="end"
            fontSize="10"
            fill="hsl(var(--fg-subtle))"
            fontFamily="var(--font-euclid)"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatValue(tick.v)}
          </text>
        ))}

        {/* X axis labels */}
        {showAxes && data.map((d, i) => {
          // Show ~5 labels max
          const stride = Math.max(1, Math.floor(data.length / 5));
          if (i % stride !== 0 && i !== data.length - 1) return null;
          const x = padL + (i / Math.max(data.length - 1, 1)) * innerW;
          return (
            <text
              key={`xlbl-${i}`}
              x={x}
              y={padT + innerH + 16}
              textAnchor="middle"
              fontSize="10"
              fill="hsl(var(--fg-subtle))"
              fontFamily="var(--font-euclid)"
            >
              {d.label}
            </text>
          );
        })}

        {/* Hover crosshair */}
        {hoverIdx != null && (() => {
          const x = padL + (hoverIdx / Math.max(data.length - 1, 1)) * innerW;
          return (
            <line
              x1={x}
              x2={x}
              y1={padT}
              y2={padT + innerH}
              stroke="hsl(var(--fg-subtle))"
              strokeWidth="0.75"
              strokeDasharray="2 2"
              opacity="0.6"
            />
          );
        })()}

        {/* Hover dots per series */}
        {hoverIdx != null && series_.map((s) => {
          const pts = points(s.key);
          const p = pts[hoverIdx];
          if (!p) return null;
          return (
            <circle
              key={`dot-${s.key}`}
              cx={p[0]}
              cy={p[1]}
              r="4"
              fill="hsl(var(--bg))"
              stroke={s.color}
              strokeWidth="2"
            />
          );
        })}

        {/* Invisible hover areas */}
        {data.map((_, i) => {
          const x1 = padL + (i / Math.max(data.length - 1, 1)) * innerW - (innerW / data.length) / 2;
          const x2 = x1 + innerW / data.length;
          return (
            <rect
              key={`hover-${i}`}
              x={x1}
              y={padT}
              width={x2 - x1}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
            />
          );
        })}
      </svg>

      {/* Tooltip */}
      {hoverIdx != null && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 mt-2 px-3 py-2 rounded-md bg-surface-2 border border-border-strong shadow text-xs"
          style={{
            left: `${((padL + (hoverIdx / Math.max(data.length - 1, 1)) * innerW) / width) * 100}%`,
            position: 'relative',
            marginTop: '-50px',
          }}
        >
          <div className="text-fg-subtle mb-1">{data[hoverIdx].label}</div>
          {series_.map((s) => (
            <div key={s.key} className="flex items-center gap-2">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: s.color }}
              />
              <span className="text-fg-muted">{s.label}</span>
              <span className="ml-auto text-fg font-medium tabular-nums">
                {formatNumber(data[hoverIdx][s.key] || 0)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}